import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch, RootState } from '../../../../store/store';
import InteractiveButton from '../../../InteractiveButton/InteractiveButton';
import { generatePath, useNavigate } from 'react-router-dom';
import ROUTE from '../../../../constants/route-constants';
import { EXTENDED_INTERVAL_UPDATE_DATA } from '../../../../constants/common';
import { ChangeChannelAction } from '../../../../enums/action';
import { changeChannelAction } from '../../../../pages/EditController/SubPages/EditChannel/channelActions';
import { FaultTextPresenter } from './FaultTextPresenter';
import { CHANNEL_ACTIONS_BY_ROLE } from '../../../../constants/channel-allowed-actions';
import { useTranslation } from 'react-i18next';

type Props = {
  controllerId: string;
  channelNum: number;
  manualFaultMessage?: string;
};
type StateProps = ReturnType<typeof mapState>;
type DispatchProps = ReturnType<typeof mapDispatch>;
type ComponentProps = StateProps & DispatchProps & Props;

const ChannelFaultsModalView = (props: ComponentProps) => {
  const { t } = useTranslation();
  const [faults, setFaults] = useState<string[]>([]);
  const navigate = useNavigate();
  const { controllerId, channelNum, getFaults, user, manualFaultMessage } = props;

  const allowedActionsForUser = useMemo(() => {
    if (!user?.memberData?.role) {
      return [];
    }

    return CHANNEL_ACTIONS_BY_ROLE[user.memberData.role];
  }, [user]);

  const fetchFaults = useCallback(async () => {
    const { channelFaults } = await getFaults({ controller_id: controllerId, channel_num: channelNum });
    setFaults(channelFaults.messages.value);
  }, [controllerId, channelNum]);

  const onClickBtnHandler = () => {
    const path = generatePath(ROUTE.EDIT_CONTROLLER_FAULT_CONTROL, { id: controllerId });
    navigate(path);
  };

  const allFaults = useMemo(() => {
    const apiFaults = faults;
    const manualFaults = manualFaultMessage ? [manualFaultMessage] : [];
    return [...apiFaults, ...manualFaults];
  }, [faults, manualFaultMessage]);

  const hasManualFaultsOnly = useMemo(() => {
    return faults.length === 0 && !!manualFaultMessage;
  }, [faults, manualFaultMessage]);

  const onClickClearFaults = () => {
    changeChannelAction(controllerId, channelNum, ChangeChannelAction.RESET_CHANNEL_FAULTS, '', hasManualFaultsOnly);
  };

  useEffect(() => {
    fetchFaults();
    const interval = setInterval(fetchFaults, EXTENDED_INTERVAL_UPDATE_DATA);

    return () => {
      clearInterval(interval);
      setFaults([]);
    };
  }, [fetchFaults]);

  if (!allFaults.length) {
    return null;
  }

  return (
    <div className='c-channel-faults'>
      <FaultTextPresenter faultText={allFaults} title={t('channels.faultsModal.title', '', { chNum: channelNum })} />
      <div className="flex w-full justify-center">
        <div className="flex flex-col gap-2">
          <InteractiveButton onClick={onClickBtnHandler}>
            {t('channels.faultsModal.btns.navigateToFaults')}
          </InteractiveButton>
          {/* Try implement CASL logic in the future */}
          {allowedActionsForUser.includes(ChangeChannelAction.RESET_CHANNEL_FAULTS) && (
            <InteractiveButton onClick={onClickClearFaults}>
              {t('channels.faultsModal.btns.clearFaults')}
            </InteractiveButton>
          )}
        </div>
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({ user: state.user.profile });

const mapDispatch = (dispatch: Dispatch) => ({
  getFaults: dispatch.channels.getChannelFaults,
});

export const ChannelFaults = connect<StateProps, DispatchProps, Props>(mapState, mapDispatch)(ChannelFaultsModalView);
